import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { PickScamResult } from "../types";

/* ================= Styled Components ================= */

const Page = styled.div`
  position: relative;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  color: #e9eefc;
  z-index: 10;
  align-items: center;
  padding: 20px 16px 40px;
  box-sizing: border-box;
`;

const Hero = styled.section`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ResultTitle = styled.h1<{ $isCorrect: boolean }>`
  font-family: "PingFang TC", system-ui, sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 42px;
  line-height: 1.2;
  text-align: center;
  margin: 0 0 8px 0;
  background: ${({ $isCorrect }) =>
    $isCorrect
      ? "linear-gradient(90deg, #39daff 0%, #1f70cb 100%)"
      : "linear-gradient(90deg, #FF5D39 0%, #CB1F1F 100%)"};
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0px 4px 12px rgba(0, 0, 0, 0.4);

  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 64px;
  }
`;

const ResultSubtitle = styled.p<{ $isCorrect: boolean }>`
  font-family: "PingFang TC", system-ui, sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 26px;
  line-height: 1.4;
  letter-spacing: 1px;
  text-align: center;
  margin: 0;
  background: linear-gradient(
    90deg,
    #757ea7 -8.1%,
    #ffffff 53.63%,
    #3e466d 108.63%
  );
  -webkit-text-fill-color: transparent;
  background-clip: text;
  opacity: 0.9;

  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 28px;
  }
`;

const CharacterImage = styled.img`
  width: 240px;
  object-fit: contain;
  user-select: none;
  z-index: 2;

  @media (min-width: 768px) and (max-width: 1024px) {
    width: 280px;
    height: 280px;
  }
`;

const WatermarkText = styled.div<{ $isCorrect: boolean }>`
  white-space: nowrap;
  pointer-events: none;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  font-family: "PingFang TC", sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 70px;
  line-height: 1;
  text-align: center;
  letter-spacing: 1px;
  background: linear-gradient(
    92.81deg,
    rgba(255, 255, 255, 0) -3.72%,
    rgba(255, 255, 255, 0.8) 99.11%
  );
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-transform: uppercase;
  opacity: 0.8;

  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 120px;
  }
`;

/* ===== Acrylic Notice Box ===== */

const AcrylicBox = styled.div`
  width: 85%;
  max-width: 600px;
  margin: -70px auto 40px;
  padding: 24px;
  border-radius: 20px;
  z-index: 3;

  /* Glassmorphism / Acrylic effect */
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);

  /* Border with subtle gradient */
  border: 1px solid rgba(255, 255, 255, 0.1);

  /* Shadow for depth */
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);

  @media (min-width: 768px) and (max-width: 1024px) {
    padding: 32px;
    border-radius: 28px;
  }
`;

const NoticeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 16px;
`;

const NoticeIcon = styled.img`
  width: 24px;
  height: 24px;
  display: block;

  @media (min-width: 768px) and (max-width: 1024px) {
    width: 32px;
    height: 32px;
  }
`;

const NoticeTitleText = styled.div`
  font-family: "PingFang TC", system-ui, sans-serif;
  font-weight: 500;
  font-size: 20px;
  line-height: 1.4;
  letter-spacing: 1px;
  color: #ffffff;
  text-shadow: 0px 2px 8px rgba(0, 0, 0, 0.3);

  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 26px;
  }
`;

const NoticeText = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
  font-family: "PingFang TC", system-ui, sans-serif;
  font-weight: 400;
  font-size: 16px;
  /* line-height: 1.7; */
  letter-spacing: 0.5px;
  text-align: left;
  white-space: pre-line;
  text-shadow: 0px 4px 14.1px rgba(13, 15, 58, 0.52);

  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 20px;
    line-height: 1.8;
  }
`;

/* ========== Public Information (Horizontal Scroll) ========== */

const PublicInfoSection = styled.section`
  width: 100%;
  margin-top: 20px;
`;

const PublicInfoTitle = styled.h4`
  font-family: "PingFang TC", system-ui, sans-serif;
  font-weight: 500;
  font-size: 22px;
  line-height: 1.2;
  letter-spacing: 1px;
  background: linear-gradient(
    90deg,
    #757ea7 -8.1%,
    #ffffff 53.63%,
    #3e466d 108.63%
  );
  -webkit-text-fill-color: transparent;
  -webkit-background-clip: text;
  background-clip: text;
  text-align: center;
  margin: 0 0 16px 0;
  padding-left: 0;

  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 28px;
  }
`;

const CarouselWrap = styled.div`
  width: 100%;
  display: flex;
  gap: 12px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  padding: 6px 0 10px;
  -webkit-overflow-scrolling: touch;

  /* Hide scrollbar */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const CarouselCard = styled.a`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 0 0 auto;
  width: 260px;
  min-height: 140px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  scroll-snap-align: start;
  position: relative;
  padding: 20px;
  text-decoration: none;
  box-sizing: border-box;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.35);
  }

  @media (min-width: 768px) and (max-width: 1024px) {
    width: 360px;
    min-height: 180px;
    padding: 26px;
  }
`;

const CarouselText = styled.div`
  font-family: "PingFang TC", system-ui, sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 15px;
  line-height: 1.6;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;

  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 18px;
    line-height: 1.7;
  }
`;

const CarouselDate = styled.div`
  font-family: "PingFang TC", system-ui, sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.4;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.6);

  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 16px;
  }
`;

/* ================= Component ================= */

// Placeholder data for public information cards
const PLACEHOLDER_NEWS = [
  {
    id: 1,
    text: "張麗善沒賣這個！網瘋傳「海苔南瓜脆片」是AI假貨，縣府急報警：千萬別下單",
    date: "2025/11/13",
    url: "https://www.storm.mg/article/11080899",
  },
];

export default function ScamResultPage() {
  const navigate = useNavigate();
  const { state } = useLocation() as {
    state?: PickScamResult & { isCorrect?: boolean };
  };

  // If no state, redirect back
  useEffect(() => {
    if (!state) {
      console.warn("No state found, redirecting to home");
      navigate("/county-magistrate");
    }
  }, [state, navigate]);

  if (!state) {
    return null;
  }

  // Determine if the user answered correctly
  const isCorrect = state.isCorrect ?? false;

  // Configuration based on result
  const config = isCorrect
    ? {
        title: "厲害!",
        subtitle: "您找出了詐騙訊息",
        characterImage: "/images/li-shan.png",
        watermark: "SECURITY",
        noticeText:
          "縣長絕不會以任何形式為特定產品、服務或商業活動進行推薦、背書或代言。\n\n保護您的財產與個資，從小心查證做起！",
      }
    : {
        title: "上當了!",
        subtitle: "您沒找出詐騙訊息",
        characterImage: "/images/hacker.png",
        watermark: "SC　AM",
        noticeText:
          "縣長絕不會以任何形式為特定產品、服務或商業活動進行推薦、背書或代言。\n\n保護您的財產與個資，從小心查證做起！",
      };

  return (
    <Page>
      {/* Hero Section with Result */}
      <Hero>
        <WatermarkText $isCorrect={isCorrect}>{config.watermark}</WatermarkText>

        <ResultTitle $isCorrect={isCorrect}>{config.title}</ResultTitle>
        <ResultSubtitle $isCorrect={isCorrect}>
          {config.subtitle}
        </ResultSubtitle>

        <CharacterImage
          src={config.characterImage}
          alt={isCorrect ? "正確" : "錯誤"}
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      </Hero>

      {/* Acrylic Notice Box */}
      <AcrylicBox>
        <NoticeHeader>
          <NoticeIcon src="/images/sparkles-logo.svg" alt="提醒圖示" />
          <NoticeTitleText>提醒您</NoticeTitleText>
        </NoticeHeader>
        <NoticeText>{config.noticeText}</NoticeText>
      </AcrylicBox>

      {/* Public Information Section */}
      <PublicInfoSection>
        <PublicInfoTitle>公開資訊</PublicInfoTitle>
        <CarouselWrap>
          {PLACEHOLDER_NEWS.map((item) => (
            <CarouselCard key={item.id} href={item.url ?? "#"} target="_blank">
              <CarouselText>{item.text}</CarouselText>
              <CarouselDate>{item.date}</CarouselDate>
            </CarouselCard>
          ))}
        </CarouselWrap>
      </PublicInfoSection>
    </Page>
  );
}
